import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getTrainingSession,
  getTrainingSessionMetricHistory,
  getTrainingSessionMetrics,
  type TrainingSessionDetailView,
  type TrainingSessionListView,
  type TrainingSessionMetricHistoryView,
  type TrainingSessionMetricsView,
} from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset";
import { createLogger } from "@/logging";
import { isActiveSessionStatus } from "@/modules/training/public";
import { historyStateKey } from "./sessionCompareModel";

const logger = createLogger("page/training/compare");

// 세션 상세 창(statusPollMs)·곡선(metricsPollMs)과 같은 주기다. 서버 metric 캐시가 30s SWR 이라
// 더 짧게 폴링해도 더 최신 값을 받지 못한다.
const comparePollMs = 15_000;

export type SessionCompareLoad = {
  detail: LoadState<TrainingSessionDetailView>;
  metrics: LoadState<TrainingSessionMetricsView>;
};

export type SessionCompareController = {
  // sessionId → 상세·지표 키 목록. 도착하는 순서대로 부분 적용한다.
  loads: Record<string, SessionCompareLoad>;
  // historyStateKey(sessionId, metricKey) → 시계열.
  histories: Record<string, LoadState<TrainingSessionMetricHistoryView>>;
  // 활성 세션이 남아 있는지. 헤더의 "실시간 갱신 중" 표기가 읽는다.
  live: boolean;
  loading: boolean;
  loadFailed: boolean;
  retry: () => void;
};

function fromResult<T>(result: PromiseSettledResult<T>): LoadState<T> {
  return result.status === "fulfilled"
    ? { status: "success", data: result.value }
    : { status: "error", message: resolveLoadErrorMessage(result.reason) };
}

/**
 * 선택 세션 N개의 상세, 지표 키, 선택 지표 시계열을 병렬로 받는다. 세션 배치 API 가 없으므로
 * 세션 단위 API 를 N번 호출하는 wave 로 조립한다.
 *
 * - `enabled` 가 꺼지면(창 최소화) 폴링을 멈추고 받은 데이터는 유지한다. 복원하면 전체를 다시 받는다.
 * - 활성 세션이 하나라도 있으면 15s 체인드 setTimeout 으로 갱신한다. 종단으로 확인된 세션은
 *   데이터가 바뀌지 않으므로 폴링 wave 에서 제외한다.
 * - 폴링 갱신은 성공 데이터를 유지한 채 교체한다. 그래서 로딩 표시가 깜빡이지 않는다.
 * - 지표 토글과 재시도는 전체 wave 를 다시 실행한다.
 */
export function useSessionCompareController(
  sessions: readonly TrainingSessionListView[],
  options: { enabled: boolean; selectedKeys: readonly string[] },
): SessionCompareController {
  const { enabled, selectedKeys } = options;
  const [loads, setLoads] = useState<Record<string, SessionCompareLoad>>({});
  const [histories, setHistories] = useState<
    Record<string, LoadState<TrainingSessionMetricHistoryView>>
  >({});
  const [refreshKey, setRefreshKey] = useState(0);

  // 배열 identity 가 바뀌어도 effect 가 다시 시작하지 않도록 정렬·조인한 문자열로 줄인다.
  const idsKey = useMemo(() => sessions.map((session) => session.id).join("\n"), [sessions]);
  const wantedKeysKey = useMemo(() => [...new Set(selectedKeys)].sort().join("\n"), [selectedKeys]);

  // 폴링 대상을 판정하는 최신 status. state 를 effect 의존성으로 만들지 않으려고 ref 에 둔다.
  // 상세의 status 는 nullable 이다. null 이면 "비활성 확인"으로 본다.
  const statusRef = useRef<Record<string, string | null>>({});

  useEffect(() => {
    if (!enabled || idsKey === "") {
      return;
    }
    const ids = idsKey.split("\n");
    const wantedKeys = wantedKeysKey === "" ? [] : wantedKeysKey.split("\n");
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // fullWave(첫 로드·재시도·지표 변경·복원)는 모든 세션을 받는다. 폴링 wave 는 종단으로 확인된
    // 세션을 제외한다. 종단 세션의 상세와 지표는 바뀌지 않는다. status 를 확인하지 못한 세션(상세
    // 실패 포함)은 wave 마다 다시 시도한다.
    async function load(fullWave: boolean): Promise<void> {
      const targets = ids.filter((id) => {
        if (fullWave) {
          return true;
        }
        const status = statusRef.current[id];
        return status === undefined || isActiveSessionStatus(status);
      });

      // 폴링 갱신은 기존 성공 데이터를 유지한다. 첫 로드만 로딩으로 보인다.
      setLoads((prev) => {
        const next = { ...prev };
        for (const id of targets) {
          next[id] = {
            detail: next[id]?.detail.status === "success" ? next[id].detail : { status: "loading" },
            metrics:
              next[id]?.metrics.status === "success" ? next[id].metrics : { status: "loading" },
          };
        }
        return next;
      });
      setHistories((prev) => {
        const next = { ...prev };
        for (const id of targets) {
          for (const key of wantedKeys) {
            const stateKey = historyStateKey(id, key);
            if (next[stateKey]?.status !== "success") {
              next[stateKey] = { status: "loading" };
            }
          }
        }
        return next;
      });

      // 세션 축도 병렬로 받는다. 각 세션의 응답 묶음이 도착하는 대로 부분 적용한다.
      await Promise.all(
        targets.map(async (id) => {
          const [detailResult, metricsResult, ...historyResults] = await Promise.allSettled([
            getTrainingSession(id),
            getTrainingSessionMetrics(id),
            ...wantedKeys.map((key) => getTrainingSessionMetricHistory(id, key)),
          ]);
          if (cancelled) {
            return;
          }
          if (detailResult.status === "fulfilled") {
            statusRef.current[id] = detailResult.value.status;
          } else {
            logger.error(`compare detail load failed: ${id}`, detailResult.reason);
          }
          setLoads((prev) => ({
            ...prev,
            [id]: { detail: fromResult(detailResult), metrics: fromResult(metricsResult) },
          }));
          setHistories((prev) => {
            const next = { ...prev };
            wantedKeys.forEach((key, index) => {
              next[historyStateKey(id, key)] = fromResult(historyResults[index]);
            });
            return next;
          });
        }),
      );
      if (cancelled) {
        return;
      }
      // 활성으로 확인된 세션이 있을 때만 다음 wave 를 실행한다. 모두 종단이면 폴링을 멈춘다.
      if (ids.some((id) => isActiveSessionStatus(statusRef.current[id] ?? null))) {
        timer = setTimeout(() => void load(false), comparePollMs);
      }
    }

    void load(true);
    return () => {
      cancelled = true;
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [enabled, idsKey, wantedKeysKey, refreshKey]);

  const retry = useCallback(() => {
    setRefreshKey((key) => key + 1);
  }, []);

  // 상세가 오기 전에는 목록 행 스냅샷의 status 로 판정한다. 그래서 폴링 시작 전 첫 렌더도 표기가 맞다.
  const live = useMemo(
    () =>
      sessions.some((session) => {
        const detail = loads[session.id]?.detail;
        const status = detail?.status === "success" ? detail.data.status : session.status;
        return isActiveSessionStatus(status);
      }),
    [sessions, loads],
  );

  // 집계는 현재 선택 축(세션 × 선택 지표)만 본다. 토글로 끈 지표의 상태가 남아 있어도
  // 배너와 로딩 표기에 영향을 주지 않는다.
  const states: LoadState<unknown>[] = [
    ...sessions.flatMap((session) => {
      const load = loads[session.id];
      return load === undefined ? [] : [load.detail, load.metrics];
    }),
    ...sessions.flatMap((session) =>
      selectedKeys
        .map((key) => histories[historyStateKey(session.id, key)])
        .filter(
          (state): state is LoadState<TrainingSessionMetricHistoryView> => state !== undefined,
        ),
    ),
  ];

  return {
    loads,
    histories,
    live,
    loading: states.some((state) => state.status === "loading"),
    loadFailed: states.some((state) => state.status === "error"),
    retry,
  };
}
